<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, onBeforeUnmount, onMounted, ref, type ComputedRef } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import EditorAuthDialogShell from '@/components/auth/EditorAuthDialogShell.vue'
import EditorProfileDialog from '@/components/auth/EditorProfileDialog.vue'
import ImockupLogo from '@/components/imockup/ImockupLogo.vue'
import { useEditorAuth } from '@/composables/useEditorAuth'
import { useEditorAuthDialog } from '@/composables/useEditorAuthDialog'
import { LOCALE_MENU_ITEMS, type UiLocale } from '@/i18n/schema'
import { marketingPathForEditorUiLocale, uiLocaleToEditorRouterPath } from '@/router/editorUrlLocale'
import { useMockupEditorStore } from '@/stores/mockupEditor'

const route = useRoute()
const router = useRouter()
const store = useMockupEditorStore()
const { locale, workspaceMode } = storeToRefs(store)
const langMenuRef = ref<HTMLElement | null>(null)
const langMenuOpen = ref(false)
const { authDialogOpen, authDialogMode, openEditorAuthDialog } = useEditorAuthDialog()
const profileDialogOpen = ref(false)
const { isLoggedIn, userEmail } = useEditorAuth()
type MarketingNavItem = {
  ariaLabel: ComputedRef<string>
  path: string
  iconPath: string
}

const marketingNavItems: MarketingNavItem[] = [
  {
    ariaLabel: computed(() => store.tUi('nav.marketingHome')),
    path: '/',
    iconPath: 'M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z',
  },
  {
    ariaLabel: computed(() => store.tUi('nav.marketingTemplates')),
    path: '/templates',
    iconPath:
      'M3 5.5A2.5 2.5 0 0 1 5.5 3h13A2.5 2.5 0 0 1 21 5.5v13a2.5 2.5 0 0 1-2.5 2.5h-13A2.5 2.5 0 0 1 3 18.5v-13Zm2.5-.5a.5.5 0 0 0-.5.5V10h14V5.5a.5.5 0 0 0-.5-.5h-13ZM19 12H5v6.5c0 .28.22.5.5.5h13a.5.5 0 0 0 .5-.5V12Z',
  },
  {
    ariaLabel: computed(() => store.tUi('nav.marketingTutorials')),
    path: '/tutorials',
    iconPath:
      'M12 6.5c-1.93 0-3.5 1.57-3.5 3.5v8.5h7V10c0-1.93-1.57-3.5-3.5-3.5Zm0-2A5.5 5.5 0 0 1 17.5 10v9.25c0 .97-.78 1.75-1.75 1.75h-7.5A1.75 1.75 0 0 1 6.5 19.25V10A5.5 5.5 0 0 1 12 4.5ZM9 3h6v2H9V3Z',
  },
  {
    ariaLabel: computed(() => store.tUi('nav.marketingPricing')),
    path: '/pricing',
    iconPath:
      'M6 4h12v2H6V4Zm-1 5a3 3 0 0 1 3-3h8a3 3 0 1 1 0 6H9a1 1 0 0 0 0 2h7a3 3 0 1 1 0 6H8a3 3 0 1 1 0-6h7a1 1 0 1 0 0-2H8a3 3 0 0 1-3-3Z',
  },
  {
    ariaLabel: computed(() => store.tUi('nav.marketingContact')),
    path: '/contact',
    iconPath:
      'M4 5.5A2.5 2.5 0 0 1 6.5 3h11A2.5 2.5 0 0 1 20 5.5v13a2.5 2.5 0 0 1-2.5 2.5h-11A2.5 2.5 0 0 1 4 18.5v-13Zm2.5-.5a.5.5 0 0 0-.5.5V7l6 4 6-4V5.5a.5.5 0 0 0-.5-.5h-11ZM18 9.44l-5.43 3.62a1 1 0 0 1-1.14 0L6 9.44v9.06c0 .28.22.5.5.5h11a.5.5 0 0 0 .5-.5V9.44Z',
  },
  {
    ariaLabel: computed(() => store.tUi('nav.marketingConsult')),
    path: '/consult',
    iconPath:
      'M12 3c-4.97 0-9 3.58-9 8 0 2.28 1.08 4.33 2.81 5.79L5 21l4.42-2.21c.83.14 1.69.21 2.58.21 4.97 0 9-3.58 9-8s-4.03-8-9-8Zm-4 9h8v2H8v-2Zm0-4h8v2H8V8Z',
  },
]

/**
 * 全局快捷键：撤销 / 重做（输入控件内不拦截）。
 *
 * @param ev - 键盘事件
 */
function onGlobalKeydown(ev: KeyboardEvent): void {
  const target = ev.target as HTMLElement | null
  const tag = target?.tagName
  if (target?.isContentEditable || tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return
  if (ev.key === 'Delete' || ev.key === 'Backspace') {
    if (store.selectedLayerId) {
      ev.preventDefault()
      store.removeActiveMockupLayer()
      return
    }
    if (store.selectedClipId) {
      ev.preventDefault()
      store.removeSelectedAnimationClip()
      return
    }
  }
  const meta = ev.ctrlKey || ev.metaKey
  if (!meta) return
  if (ev.key === 'z' && !ev.shiftKey) {
    ev.preventDefault()
    store.undoEditor()
    return
  }
  if (ev.key === 'y' || (ev.key === 'z' && ev.shiftKey)) {
    ev.preventDefault()
    store.redoEditor()
  }
}

onMounted(() => {
  globalThis.addEventListener('keydown', onGlobalKeydown)
  globalThis.addEventListener('pointerdown', onGlobalPointerDown)
  globalThis.addEventListener('keydown', onGlobalEscCloseMenus)
})

onBeforeUnmount(() => {
  globalThis.removeEventListener('keydown', onGlobalKeydown)
  globalThis.removeEventListener('pointerdown', onGlobalPointerDown)
  globalThis.removeEventListener('keydown', onGlobalEscCloseMenus)
})

/**
 * 获取营销站首页地址（用于编辑器「返回官网」与个人中心、订阅详情等 Nitro 代理调用）。
 *
 * **经 `marketingOrigin` 访问的 Nitro 路径盘点**（须由营销站 Nuxt 提供，见 `apps/marketing/server/api`）：
 * - `GET /api/entitlement/current`（`EditorProfileDialog` 会员快照）
 * - `GET /api/subscription/query`（订阅详情）
 * - `GET /api/payment/order/list`（订单分页）
 * - `POST /api/subscription/cancel`（周期末取消）
 *
 * **解析顺序**：
 * - `VITE_MARKETING_ORIGIN` 存在时优先使用（生产推荐）
 * - 本地开发从 `:5173` 自动映射到 `:3000`
 * - 其它场景回退为当前页 `location.host`（若无 Nitro `/api` 将出现 HTML 404）
 */
function resolveMarketingHomeUrl(): string {
  const configured = import.meta.env.VITE_MARKETING_ORIGIN
  if (typeof configured === 'string' && configured.trim().length > 0) {
    return configured.trim().replace(/\/$/, '')
  }

  const { protocol, hostname, port } = globalThis.location
  if (port === '5173') {
    return `${protocol}//${hostname}:3000`
  }

  if (import.meta.env.DEV) {
    console.warn(
      '[imockup-editor] 未设置 VITE_MARKETING_ORIGIN，已回退为当前页 host。订阅详情等将请求同源 /api/*；若该主机无 Nitro 代理将出现 404。请在 .env 中设置 VITE_MARKETING_ORIGIN 指向营销站源地址。',
    )
  }

  return `${protocol}//${globalThis.location.host}`
}

/**
 * 拼接营销站链接地址（支持首页与其他路径）。
 *
 * @param path - 营销站路径（以 `/` 开头）
 */
function resolveMarketingUrl(path: string): string {
  const base = resolveMarketingHomeUrl()
  const localized = marketingPathForEditorUiLocale(path, store.locale)
  if (localized === '/') return base
  return `${base}${localized}`
}

/**
 * 从编辑器跳转到营销站指定页面：使用当前页跳转（不新开标签）。
 *
 * @param path - 营销站路径（以 `/` 开头）
 */
function goToMarketingPath(path: string): void {
  globalThis.location.assign(resolveMarketingUrl(path))
}

/**
 * 打开/关闭语言二级菜单。
 */
function toggleLangMenu(): void {
  langMenuOpen.value = !langMenuOpen.value
}

/**
 * 选择语言并关闭菜单（由路由 `beforeEach` 同步 `vue-i18n` 与本地缓存）。
 *
 * @param next - 目标语言
 */
async function onSelectLocale(next: UiLocale): Promise<void> {
  langMenuOpen.value = false
  const path = uiLocaleToEditorRouterPath(next)
  if (route.path === path) return
  await router.push({ path, query: route.query, hash: route.hash })
}

/**
 * 点击菜单外部时关闭语言菜单。
 *
 * @param ev - 鼠标事件
 */
function onGlobalPointerDown(ev: MouseEvent): void {
  const target = ev.target as Node | null
  if (!target) return
  if (langMenuOpen.value) {
    const host = langMenuRef.value
    if (host && !host.contains(target)) langMenuOpen.value = false
  }
}

/**
 * `Escape` 关闭二级菜单。
 *
 * @param ev - 键盘事件
 */
function onGlobalEscCloseMenus(ev: KeyboardEvent): void {
  if (ev.key !== 'Escape') return
  if (langMenuOpen.value) langMenuOpen.value = false
  if (profileDialogOpen.value) profileDialogOpen.value = false
}

/**
 * 当前语言标签（用于图标按钮 title）。
 */
const localeLabel = computed(
  () => LOCALE_MENU_ITEMS.find((item) => item.code === locale.value)?.nativeName ?? 'Language',
)

/**
 * 当前工作模式标签（用于图标按钮 title）。
 */
const workspaceModeLabel = computed(() =>
  workspaceMode.value === 'default' ? store.tUi('nav.modeDefault') : store.tUi('nav.modeEdit'),
)

/**
 * 账户入口提示文案（登录后显示邮箱，未登录显示多语言提示）。
 */
const accountEntryLabel = computed(() => userEmail.value || store.tUi('nav.accountEntry'))

/**
 * 点击用户图标：未登录弹登录，已登录弹账户菜单。
 */
function onProfileClick(): void {
  if (!isLoggedIn.value) {
    openEditorAuthDialog('login')
    return
  }
  profileDialogOpen.value = true
}
</script>

<template>
  <header class="nav">
    <div class="inner">
      <div class="left">
        <nav class="quickNav" aria-label="营销站入口">
          <button
            v-for="item in marketingNavItems"
            :key="item.path"
            type="button"
            class="ico quickNavItem"
            :aria-label="item.ariaLabel.value"
            :title="item.ariaLabel.value"
            :data-tooltip="item.ariaLabel.value"
            @click="goToMarketingPath(item.path)"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
              <path fill="currentColor" :d="item.iconPath" />
            </svg>
          </button>
        </nav>
      </div>

      <div class="brand">
        <ImockupLogo />
      </div>

      <div class="right">
        <button
          type="button"
          class="ico"
          :aria-label="accountEntryLabel"
          :title="accountEntryLabel"
          :data-tooltip="accountEntryLabel"
          :aria-expanded="isLoggedIn && profileDialogOpen ? 'true' : 'false'"
          :aria-haspopup="isLoggedIn ? 'dialog' : 'false'"
          @click="onProfileClick"
        >
          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
            <path
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M20 21a8 8 0 0 0-16 0M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z"
            />
          </svg>
        </button>
        <div ref="langMenuRef" class="menuWrap">
          <button
            type="button"
            class="ico"
            :aria-label="store.tUi('nav.lang')"
            :title="`${store.tUi('nav.lang')} · ${localeLabel}`"
            :data-tooltip="`${store.tUi('nav.lang')} · ${localeLabel}`"
            :aria-expanded="langMenuOpen ? 'true' : 'false'"
            aria-haspopup="menu"
            @click="toggleLangMenu"
          >
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
              <path
                fill="currentColor"
                d="M12.87 15.07 11 13.2a11.6 11.6 0 0 0 2.81-5.06h2.14V6h-6V4h-2v2H2v2h9.75a9.9 9.9 0 0 1-2.35 3.98c-.74-.82-1.36-1.73-1.85-2.71H5.55a13.4 13.4 0 0 0 2.5 4l-3.7 3.69L5.77 18l3.63-3.63L11.66 17l1.21-1.93ZM17.5 10h-2L11 22h2l1.12-3h4.75L20 22h2l-4.5-12Zm-2.61 7 1.73-4.62L18.36 17h-3.47Z"
              />
            </svg>
          </button>
          <div v-if="langMenuOpen" class="menuPanel" role="menu" :aria-label="store.tUi('nav.lang')">
            <button
              v-for="item in LOCALE_MENU_ITEMS"
              :key="item.code"
              type="button"
              class="menuItem"
              role="menuitemradio"
              :aria-checked="locale === item.code ? 'true' : 'false'"
              :class="{ active: locale === item.code }"
              @click="onSelectLocale(item.code)"
            >
              {{ item.nativeName }}
            </button>
          </div>
        </div>
        <button
          type="button"
          class="ico icoMode"
          :class="{ active: workspaceMode === 'edit' }"
          :aria-label="store.tUi('nav.mode')"
          :aria-pressed="workspaceMode === 'edit' ? 'true' : 'false'"
          :title="`${store.tUi('nav.mode')} · ${workspaceModeLabel}`"
          :data-tooltip="`${store.tUi('nav.mode')} · ${workspaceModeLabel}`"
          @click="store.toggleWorkspaceMode()"
        >
          <svg class="modeGlyph" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
            <path fill="currentColor" d="M4 5a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v4h-2V5H6v14h12v-4h2v4a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5Z" />
            <path class="modeArrow arrowDefault" fill="currentColor" d="M14.59 9.59 16 11l-3 3 3 3-1.41 1.41L10.17 14l4.42-4.41Z" />
            <path class="modeArrow arrowEdit" fill="currentColor" d="m9.41 9.59-1.42 1.42L12.41 14l-4.42 4.41 1.42 1.42L15.24 14 9.41 9.59Z" />
          </svg>
        </button>
      </div>
    </div>
    <EditorAuthDialogShell v-model="authDialogOpen" :default-mode="authDialogMode" />
    <EditorProfileDialog v-model="profileDialogOpen" :locale="locale" :marketing-origin="resolveMarketingHomeUrl()" />
  </header>
</template>

<style scoped>
.nav {
  position: sticky;
  top: 0;
  z-index: 1101;
  height: var(--im-header-h);
  border-bottom: 1px solid var(--im-border);
  background: var(--im-surface);
  backdrop-filter: blur(10px);
}

.inner {
  height: 100%;
  padding: 0 0.65rem;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 0.5rem;
}

.left,
.right {
  display: flex;
  align-items: center;
  gap: 0.15rem;
}

.right {
  justify-content: flex-end;
}

.menuWrap {
  position: relative;
}

.brand {
  justify-self: center;
}

.quickNav {
  display: flex;
  align-items: center;
  gap: 0.1rem;
}

.quickNavItem {
  width: 36px;
  height: 36px;
}

.ico[data-tooltip] {
  position: relative;
}

.ico[data-tooltip]::after {
  content: attr(data-tooltip);
  position: absolute;
  left: 50%;
  bottom: calc(100% + 8px);
  transform: translateX(-50%) translateY(4px);
  opacity: 0;
  pointer-events: none;
  white-space: nowrap;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  color: var(--im-text);
  background: var(--im-surface);
  border: 1px solid var(--im-border-strong);
  border-radius: 6px;
  padding: 0.28rem 0.42rem;
  box-shadow: var(--im-shadow);
  transition:
    opacity 180ms ease-out,
    transform 180ms ease-out;
  z-index: 30;
}

.ico[data-tooltip]:hover::after,
.ico[data-tooltip]:focus-visible::after {
  opacity: 1;
  transform: translateX(-50%) translateY(0);
}

.histSep {
  width: 1px;
  height: 22px;
  background: var(--im-border-strong);
  margin: 0 0.1rem;
}

.ico {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: 0;
  background: transparent;
  color: var(--im-text);
  display: grid;
  place-items: center;
  cursor: pointer;
  transition:
    transform 160ms ease-out,
    background-color 160ms ease-out,
    color 160ms ease-out,
    border-color 160ms ease-out;
}

.ico:hover:not(:disabled) {
  background: var(--im-accent-ghost);
}

.ico:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.ico:focus-visible {
  outline: 2px solid var(--im-text);
  outline-offset: 2px;
}

.ico:active:not(:disabled) {
  transform: scale(0.96);
}

.icoMode {
  position: relative;
  overflow: hidden;
}

.icoMode::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: var(--im-accent-ghost);
  opacity: 0;
  transform: scale(0.9);
  transition:
    opacity 220ms ease-out,
    transform 220ms ease-out;
  pointer-events: none;
}

.icoMode.active::after {
  opacity: 1;
  transform: scale(1);
}

.modeGlyph {
  transition:
    transform 220ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity 220ms ease-out;
}

.icoMode.active .modeGlyph {
  transform: translateX(0.5px);
}

.modeArrow {
  transition: opacity 220ms ease-out;
}

.arrowDefault {
  opacity: 1;
}

.arrowEdit {
  opacity: 0;
}

.icoMode.active .arrowDefault {
  opacity: 0;
}

.icoMode.active .arrowEdit {
  opacity: 1;
}

@media (prefers-reduced-motion: reduce) {
  .icoMode::after,
  .modeGlyph,
  .modeArrow,
  .quickNavItem,
  .ico[data-tooltip]::after {
    transition: none;
  }
}

@media (max-width: 1120px) {
  .inner {
    padding: 0 0.42rem;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 0.3rem;
  }

  .brand {
    display: none;
  }

  .left {
    min-width: 0;
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-width: thin;
    scrollbar-gutter: stable;
  }

  .quickNav {
    flex: 0 0 auto;
  }

  .quickNavItem {
    width: 34px;
    height: 34px;
  }

  .ico {
    width: 36px;
    height: 36px;
  }

  .histSep {
    display: none;
  }
}

@media (max-width: 780px) {
  .inner {
    padding: 0 0.32rem;
  }

  .right {
    gap: 0.08rem;
  }

  .ico {
    width: 34px;
    height: 34px;
  }
}

.menuPanel {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  min-width: 132px;
  border-radius: 10px;
  border: 1px solid var(--im-border);
  background: var(--im-surface);
  box-shadow: var(--im-shadow);
  padding: 0.2rem;
  display: grid;
  gap: 0.1rem;
  z-index: 20;
}

.menuItem {
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--im-text);
  text-align: left;
  font-size: 0.8125rem;
  font-weight: 700;
  padding: 0.5rem 0.6rem;
  cursor: pointer;
}

.menuItem:hover {
  background: var(--im-accent-ghost);
}

.menuItem.active {
  background: var(--im-accent-ghost);
}

.menuItem:focus-visible {
  outline: 2px solid var(--im-text);
  outline-offset: 1px;
}

</style>
